"use client";

import CustomButton from "./CustomButton";
import CallExpandBtn from "./CallExpandBtn";
import AnimateOnScroll from "./AnimateOnScroll";
import Image from "next/image";
import Quote from "../icons/Quote";
import {
  Marquee,
  MarqueeContent,
  MarqueeItem,
} from "@/components/kibo-ui/marquee";
import { SITE_URL, ORGANIZATION_NAME } from "@/lib/metadata";

const testimonials = [
  {
    id: 1,
    quote:
      "Thank you, Alex, for your invaluable guidance and support. You helped us address the gaps and challenges my partner and I were facing. Our journey has truly been enriching. As you always remind us, communication is key to a healthy relationship.",
    name: "Rk Salill",
    title: "1 month ago",
  },
  {
    id: 2,
    quote:
      "Working with Alex has been transformative. His approach to therapy is both professional and compassionate. He helped me understand my patterns and provided practical strategies for personal growth.",
    name: "Michael Chen",
    title: "3 weeks ago",
  },
  {
    id: 3,
    quote:
      "Alex's guidance during our family therapy sessions was exceptional. He created a safe space for everyone to express their feelings and helped us rebuild our relationships. Highly recommended!",
    name: "Emma Thompson",
    title: "2 months ago",
  },
  {
    id: 4,
    quote:
      "My fiancé found Alex to help me navigate through some personal family matters and my business challenges by providing me some very helpful ideas on how to better manage and process life challenges. I found Alex",
    name: "Graham Jakobasch",
    title: "1 month ago",
  },
  {
    id: 5,
    quote:
      "Alex helped us when we were going through a rough patch in our relationship. Not only did he help us get back on track and understand each other better, but he also provided us with tools to communicate more effectively. Not only did he validate our feelings, but",
    name: "Sarah Meh",
    title: "2 weeks ago",
  },
];

interface TestimonialCardProps {
  item: (typeof testimonials)[number];
}

const TestimonialCard = ({ item }: TestimonialCardProps) => (
  <article
    className="relative w-[82vw] shrink-0 overflow-hidden rounded-[7.95px] lg:w-[440px] 3xl:w-[592px]"
    aria-label={`Testimonial from ${item.name}`}
  >
    {/* Background image — fills the card */}
    <Image
      src="/images/v3/home/testimonial-bg.png"
      alt=""
      fill
      className="object-cover"
      sizes="(max-width: 640px) 82vw, (max-width: 1024px) 440px, 592px"
      aria-hidden
    />

    {/* Content sits on top of the bg image */}
    <div className="relative z-10 flex h-[400px] flex-col justify-between p-5 lg:p-[31.8px] 3xl:h-[507px]">
      {/* Quote icon + quote text */}
      <div className="flex flex-col gap-3 lg:gap-4">
        <Quote />
        <p className="font-onest text-base font-medium leading-[130%] text-v3-textV2 lg:text-lg 3xl:text-[28px]">
          {item.quote}
        </p>
      </div>

      {/* Author info */}
      <div className="flex flex-col gap-1">
        <span className="font-crimsonText text-[16px] font-bold text-v3-textV2 lg:text-[18px]">
          {item.name}
        </span>
        <span className="font-onest text-[13px] font-normal text-[#D1D1D1] lg:text-[14px]">
          {item.title}
        </span>
      </div>
    </div>
  </article>
);

const testimonialsSchema = {
  "@context": "https://schema.org",
  "@graph": testimonials.map((t) => ({
    "@type": "Review",
    author: {
      "@type": "Person",
      name: t.name,
    },
    reviewBody: t.quote,
    reviewRating: {
      "@type": "Rating",
      ratingValue: "5",
      bestRating: "5",
    },
    itemReviewed: {
      "@type": "Organization",
      name: ORGANIZATION_NAME,
      url: SITE_URL,
    },
  })),
};

const Testimonials = () => (
  <section className="bg-white" aria-labelledby="testimonials-heading">
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(testimonialsSchema).replace(/</g, "\\u003c"),
      }}
    />
    {/* ── Header ── */}
    <div className="container mb-10 flex flex-col gap-6 px-5 lg:flex-row lg:items-start lg:justify-between">
      {/* Left: subtitle + title */}
      <AnimateOnScroll className="flex flex-col gap-3">
        <span
          data-from-left
          className="font-crimsonText text-[20px] font-bold text-v3-highlight lg:text-[28px]"
        >
          Testimonial
        </span>
        <h2
          id="testimonials-heading"
          className="heading max-w-[641px] text-v3-secondary"
        >
          Client Reviews: Real Stories of Clarity, Growth & Healing in Sydney
        </h2>
      </AnimateOnScroll>

      {/* Right: description + CTAs */}
      <div className="flex w-full flex-col gap-5 lg:w-[575px] lg:shrink-0">
        <p className="font-onest text-[16px] font-normal leading-[110%] text-v3-body lg:text-[20px]">
          Read authentic experiences from individuals, couples, and families
          across Sydney who found clarity, built emotional resilience, and
          achieved personal goals through our evidence-based counselling, life
          coaching, and allied health services.
        </p>
        <div className="flex w-fit flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <CallExpandBtn />
          <CustomButton
            label="Book an Appointment"
            href="/booking"
            variant="primary"
            withIcon
            size="lg"
          />
        </div>
      </div>
    </div>

    {/* ── Marquee ── */}
    <Marquee aria-label="Testimonials">
      <MarqueeContent speed={40} pauseOnHover>
        {testimonials.map((t) => (
          <MarqueeItem key={t.id} className="mx-[10px]">
            <TestimonialCard item={t} />
          </MarqueeItem>
        ))}
      </MarqueeContent>
    </Marquee>
  </section>
);

export default Testimonials;
